import { Fragment, useState, type FunctionComponent, type ReactNode, type JSX } from 'react';
import { useFragment, graphql } from 'react-relay';
import { FormattedMessage } from 'dibs-react-intl';
import classnames from 'classnames';
import { Carousel } from 'dibs-carousel';
import styles from './styles/SellerApplicationBenefits.scss';
import Globe from 'dibs-icons/exports/legacy/Globe.ts';
import Truck from 'dibs-icons/exports/legacy/TruckMoving.ts';
import Lock from 'dibs-icons/exports/legacy/LockWithCheckmark.ts';
import Support from 'dibs-icons/exports/legacy/CustomerSupport.ts';
import Briefcase from 'dibs-icons/exports/legacy/Briefcase.ts';
import Service from 'dibs-icons/exports/legacy/ServicePerson.ts';

import { type SellerApplicationBenefits_viewer$key } from './__generated__/SellerApplicationBenefits_viewer.graphql.ts';

type BenefitItemProps = {
    icon: ReactNode;
    name: JSX.Element;
    description: JSX.Element;
    isCarouselItem?: boolean;
};
const BenefitItem: FunctionComponent<BenefitItemProps> = ({
    icon,
    name,
    description,
    isCarouselItem,
}) => (
    <div className={classnames(styles.benefitWrapper, { [styles.carouselItem]: isCarouselItem })}>
        <div className={styles.leftWrapper}>
            <div className={styles.icon}>{icon}</div>
        </div>
        <div className={styles.rightWrapper}>
            <p className={styles.name}>{name}</p>
            <p className={styles.description}>{description}</p>
        </div>
    </div>
);

const viewerFragment = graphql`
    fragment SellerApplicationBenefits_viewer on Viewer {
        newSellerPricing: featureFlag(feature: "newSellerPricing")
    }
`;

export const SellerApplicationBenefits: FunctionComponent<{
    isMobile: boolean;
    viewer: SellerApplicationBenefits_viewer$key;
}> = ({ isMobile, viewer: viewerRef }) => {
    const [carouselIndex, setCarouselIndex] = useState(0);
    const viewer = useFragment(viewerFragment, viewerRef);
    const { newSellerPricing } = viewer;
    const bullets = [
        {
            name: (
                <FormattedMessage
                    id="DealerApplicationBenefit.benefitTitle1"
                    defaultMessage="Global Audience of Qualified Customers"
                />
            ),
            description: (
                <FormattedMessage
                    id="DealerApplicationBenefit.benefit1"
                    defaultMessage="Reach millions of discerning collectors, sophisticated customers and top interior designers around the world."
                />
            ),
            icon: <Globe />,
        },
        {
            name: (
                <FormattedMessage
                    id="DealerApplicationBenefit.benefitTitle2"
                    defaultMessage="Competitive Shipping & Logistics"
                />
            ),
            description: (
                <FormattedMessage
                    id="DealerApplicationBenefit.benefit2"
                    defaultMessage="Our scale allows us to provide competitive rates worldwide. We also handle logistics, saving you valuable time."
                />
            ),
            icon: <Truck />,
        },
        {
            name: (
                <FormattedMessage
                    id="DealerApplicationBenefit.benefitTitle3"
                    defaultMessage="Guaranteed, Risk-Free Transactions"
                />
            ),
            description: (
                <FormattedMessage
                    id="DealerApplicationBenefit.benefit3"
                    defaultMessage="Our fraud and chargeback protection helps ensure safe online transactions and reduces risk for your business."
                />
            ),
            icon: <Lock />,
        },
        {
            name: (
                <FormattedMessage
                    id="DealerApplicationBenefit.benefitTitle4"
                    defaultMessage="Support Services"
                />
            ),
            description: (
                <FormattedMessage
                    id="DealerApplicationBenefit.benefit4"
                    defaultMessage="Our team is here to support you throughout the selling process and dispute resolution."
                />
            ),
            icon: <Support />,
        },
        {
            name: (
                <FormattedMessage
                    id="DealerApplicationBenefit.benefitTitle5"
                    defaultMessage="Robust Toolset"
                />
            ),
            description: (
                <FormattedMessage
                    id="DealerApplicationBenefit.benefit5"
                    defaultMessage="We provide the tools you need to grow a successful online business, keep you organized, and optimize your offering."
                />
            ),
            icon: <Briefcase />,
        },
        {
            name: newSellerPricing ? (
                <FormattedMessage
                    id="abm.sellerApplicationBenefits.successGuidance"
                    defaultMessage="Success Guidance"
                />
            ) : (
                <FormattedMessage
                    id="abm.sellerApplicationBenefits.sellerManager"
                    defaultMessage="Dedicated Seller Manager"
                />
            ),

            description: newSellerPricing ? (
                <FormattedMessage
                    id="abm.sellerApplicationBenefit.expertCoaching"
                    defaultMessage="Expert coaching and resources to allow you to optimize your business on 1stDibs."
                />
            ) : (
                <FormattedMessage
                    id="abm.sellerApplicationBenefit.expertResource"
                    defaultMessage="Your expert resource provides personalized coaching for your success and can help you solve nearly any challenge."
                />
            ),
            icon: <Service />,
        },
    ];
    return (
        <Fragment>
            <div className={styles.title}>
                <FormattedMessage
                    id="DealerApplicationBullets.title"
                    defaultMessage="Confidently Sell Online"
                />
            </div>
            <div className={styles.wrapper}>
                {isMobile ? (
                    <Carousel
                        itemsPerPage={1}
                        index={carouselIndex}
                        slideCarouselItems
                        onIndexChange={({ index }: { index: number }) => setCarouselIndex(index)}
                        totalItems={bullets.length}
                        renderItem={({ index }: { index: number }) => (
                            <BenefitItem {...bullets[index]} isCarouselItem />
                        )}
                    />
                ) : (
                    bullets.map((props, i) => <BenefitItem key={i} {...props} />)
                )}
            </div>
        </Fragment>
    );
};
